import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";
import { log } from "../../../commonfunctions/logger";
import Accordion from "../../../components/Accordion/Accordion";
import AuthenticationHOC from "../../../components/AuthenticationHOC/AuthenticationHOC";
import Button, { ButtonType } from "../../../components/Button/Button";
import CartItemTable from "../../../components/CartItemTable/CartItemTable";
import { Wrapper } from "../../../components/styled/styled-components";
import { CurrentOrder } from "../../../redux/reducers/orders";
import { RootState } from "../../../redux/store";
import ModalSwitcher from "./ModalSwitcher";

interface SaveParams {
  order_id: string;
  current_order_to_draft: boolean;
  current_order_draft_note?: string;
}

interface Props {
  order: any;
  generateAuthHeader: any;
  isOnlyOrder?: boolean;
}

const SavedOrderCard = (props: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const currentOrder: CurrentOrder = useSelector((state: RootState) => state.orders.currentOrder);
  const [draftNote, setDraftNote] = useState("");
  const { order, generateAuthHeader, isOnlyOrder = false } = props;

  /* Modal code to show
    0 - None
    1 - Are you sure you want to load this order (when trying to load order with non-empty cart)
    2 - Set order note for current cart
    3 - Are you sure you want to delete this order
  */
  const [modalToShow, setModalToShow] = useState(0);

  const deleteSavedOrder = () => {
    const url = `${process.env.REACT_APP_HALFBACK_ROOT_API}/draft_orders/delete`;
    setIsLoading(true);
    axios
      .post(url, { order_id: order.id }, generateAuthHeader())
      .then(() => {
        setModalToShow(0);
        setIsLoading(false);
        window.location.reload();
      })
      .catch((error) => {
        log(error, "Saved order deletion failed.");
        setIsLoading(false);
      });
  };

  const activateSavedOrder = (saveCurrentorder = false) => {
    const url = `${process.env.REACT_APP_HALFBACK_ROOT_API}/draft_orders/activate`;
    const params: SaveParams = {
      order_id: order.id,
      current_order_to_draft: saveCurrentorder,
    };

    if (saveCurrentorder) {
      params.current_order_draft_note = draftNote;
    }

    setIsLoading(true);
    axios
      .post(url, params, generateAuthHeader())
      .then(() => {
        setIsLoading(false);
        setModalToShow(4);
      })
      .catch((error) => {
        log(error, "Saved order activation failed");
        setIsLoading(false);
        if (error?.response?.data?.error_code === "draft_order_number_limit_reached") {
          setModalToShow(5);
        } else {
          setModalToShow(6);
        }
      });
  };

  const {
    note,
    total_items: totalItems,
    total_units: totalUnits,
    payment_details: paymentDetails,
  } = order;

  const subtotal = paymentDetails.total_products;

  return (
    <div className="saved-order-card">
      <Accordion title={note || "Untitled Order"} expanded={isOnlyOrder}>
        <div className="saved-order-content">
          {/* Order Summary and Action Buttons Section */}
          <div className="order-header">
            <div className="order-summary">
              <div className="summary-item">
                <span className="summary-label">Items:</span>
                <span className="summary-value">{totalItems}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Units:</span>
                <span className="summary-value">{totalUnits}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Subtotal:</span>
                <span className="summary-value">${parseFloat(subtotal).toFixed(2)}</span>
              </div>
            </div>
            <div className="order-actions">
              <Button
                type={ButtonType.primary}
                onClick={() => {
                  if (currentOrder.order_items.length > 0) {
                    setModalToShow(1);
                  } else {
                    activateSavedOrder();
                  }
                }}
              >
                Load Order
              </Button>
              <Button type={ButtonType.danger} onClick={() => setModalToShow(3)}>
                Delete
              </Button>
            </div>
          </div>

          {/* Order Items Table */}
          <Wrapper padding="0">
            <CartItemTable items={order.order_items} />
          </Wrapper>
        </div>
      </Accordion>
      <ModalSwitcher
        modalToShow={modalToShow}
        activateSavedOrder={activateSavedOrder}
        deleteSavedOrder={deleteSavedOrder}
        setModalToShow={setModalToShow}
        isLoading={isLoading}
        setDraftNote={setDraftNote}
      />
    </div>
  );
};

export default AuthenticationHOC(SavedOrderCard);
